import React, { useState, useEffect } from 'react';
import { getLeaderboard, getPoints, getBadges, getChallenges, joinChallenge, createChallenge } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { FiAward, FiStar, FiZap, FiFlag, FiPlus } from 'react-icons/fi';

const Gamification: React.FC = () => {
    const { user, isManager } = useAuth();
    const [tab, setTab] = useState<'leaderboard' | 'badges' | 'challenges' | 'points'>('leaderboard');
    const [leaderboard, setLeaderboard] = useState<any[]>([]);
    const [points, setPoints] = useState<any>(null);
    const [badges, setBadges] = useState<any[]>([]);
    const [challenges, setChallenges] = useState<any[]>([]);
    const [periode, setPeriode] = useState('mois');
    const [showChallengeModal, setShowChallengeModal] = useState(false);
    const [challengeForm, setChallengeForm] = useState({ nom: '', description: '', objectif_type: 'ventes', objectif_valeur: '', points_recompense: '', date_debut: '', date_fin: '' });

    useEffect(() => {
        getLeaderboard({ periode }).then(res => setLeaderboard(res.data));
    }, [periode]);

    useEffect(() => {
        getPoints('me').then(res => setPoints(res.data));
        getBadges().then(res => setBadges(res.data));
        getChallenges().then(res => setChallenges(res.data));
    }, []);

    const handleJoin = async (id: number) => {
        await joinChallenge(id);
        getChallenges().then(res => setChallenges(res.data));
    };

    const handleCreateChallenge = async (e: React.FormEvent) => {
        e.preventDefault();
        await createChallenge(challengeForm);
        setShowChallengeModal(false);
        getChallenges().then(res => setChallenges(res.data));
    };

    return (
        <div className="page-gamification">
            <div className="page-header">
                <h2><FiAward /> Gamification</h2>
                {points && <div className="points-display"><FiZap /> <strong>{points.total}</strong> points</div>}
            </div>

            <div className="tabs">
                <button className={`tab ${tab === 'leaderboard' ? 'active' : ''}`} onClick={() => setTab('leaderboard')}>Classement</button>
                <button className={`tab ${tab === 'badges' ? 'active' : ''}`} onClick={() => setTab('badges')}>Badges</button>
                <button className={`tab ${tab === 'challenges' ? 'active' : ''}`} onClick={() => setTab('challenges')}>Challenges</button>
                <button className={`tab ${tab === 'points' ? 'active' : ''}`} onClick={() => setTab('points')}>Mes Points</button>
            </div>

            {tab === 'leaderboard' && (
                <div className="leaderboard-section">
                    <div className="filters-bar">
                        <select value={periode} onChange={e => setPeriode(e.target.value)}>
                            <option value="semaine">Semaine</option><option value="mois">Mois</option><option value="annee">Annee</option><option value="tout">Tout temps</option>
                        </select>
                    </div>
                    <div className="podium">
                        {leaderboard.slice(0, 3).map((l, i) => (
                            <div key={l.id} className={`podium-item podium-${i + 1}`}>
                                <div className="podium-rank">{['🥇', '🥈', '🥉'][i]}</div>
                                <strong>{l.nom}</strong>
                                <span>{l.total_points} pts</span>
                                <small>{l.nb_badges} badges</small>
                            </div>
                        ))}
                    </div>
                    <div className="classement-list">
                        {leaderboard.slice(3).map(l => (
                            <div key={l.id} className={`classement-item ${l.id === user?.id ? 'highlight' : ''}`}>
                                <span className="rang">{l.rang}</span>
                                <span className="nom">{l.nom}</span>
                                <span className="stat">{l.total_points} pts</span>
                                <span className="stat">{l.nb_badges} badges</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {tab === 'badges' && (
                <div className="badges-grid">
                    {badges.map(b => (
                        <div key={b.id} className={`badge-card ${b.obtenu ? 'obtained' : 'locked'}`}>
                            <div className="badge-icon" style={{ color: b.obtenu ? b.couleur : '#ccc' }}><FiStar size={32} /></div>
                            <h4>{b.nom}</h4>
                            <p>{b.description}</p>
                            <span>{b.points} pts</span>
                            {b.obtenu && <small className="badge-date">Obtenu le {new Date(b.date_obtention).toLocaleDateString('fr')}</small>}
                        </div>
                    ))}
                </div>
            )}

            {tab === 'challenges' && (
                <div className="challenges-section">
                    {isManager() && <button className="btn btn-primary" onClick={() => setShowChallengeModal(true)}><FiPlus /> Nouveau challenge</button>}
                    <div className="challenges-grid">
                        {challenges.map(c => (
                            <div key={c.id} className="challenge-card">
                                <div className="challenge-header">
                                    <FiFlag color={c.actif ? '#16A34A' : '#999'} />
                                    <h4>{c.nom}</h4>
                                </div>
                                <p>{c.description}</p>
                                <div className="challenge-meta">
                                    <span>{c.objectif_type}: {c.objectif_valeur}</span>
                                    <span>{c.points_recompense} pts</span>
                                    <span>{c.nb_participants} participants</span>
                                </div>
                                <div className="challenge-dates">
                                    {new Date(c.date_debut).toLocaleDateString('fr')} - {new Date(c.date_fin).toLocaleDateString('fr')}
                                </div>
                                {c.ma_progression !== null && c.ma_progression !== undefined ? (
                                    <div className="progress-bar"><div className="progress-fill" style={{ width: `${Math.min(100, (c.ma_progression / c.objectif_valeur) * 100)}%` }}></div></div>
                                ) : (
                                    <button className="btn btn-sm btn-primary" onClick={() => handleJoin(c.id)}>Participer</button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {tab === 'points' && points && (
                <div className="points-section">
                    <div className="kpi-grid">
                        <div className="kpi-card" style={{ borderLeftColor: '#F59E0B' }}>
                            <div className="kpi-content"><span className="kpi-value">{points.total}</span><span className="kpi-label">Points total</span></div>
                        </div>
                    </div>
                    <h3>Historique</h3>
                    <div className="table-container">
                        <table className="data-table">
                            <thead><tr><th>Date</th><th>Source</th><th>Description</th><th>Points</th></tr></thead>
                            <tbody>
                                {points.historique?.map((h: any) => (
                                    <tr key={h.id}>
                                        <td>{new Date(h.created_at).toLocaleDateString('fr')}</td>
                                        <td><span className="badge">{h.source}</span></td>
                                        <td>{h.description}</td>
                                        <td><strong style={{ color: '#16A34A' }}>+{h.points}</strong></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {showChallengeModal && (
                <div className="modal-overlay" onClick={() => setShowChallengeModal(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <h3>Nouveau Challenge</h3>
                        <form onSubmit={handleCreateChallenge}>
                            <div className="form-group"><label>Nom</label><input value={challengeForm.nom} onChange={e => setChallengeForm({ ...challengeForm, nom: e.target.value })} required /></div>
                            <div className="form-group"><label>Description</label><textarea value={challengeForm.description} onChange={e => setChallengeForm({ ...challengeForm, description: e.target.value })} rows={2} /></div>
                            <div className="form-row">
                                <div className="form-group"><label>Objectif</label><input value={challengeForm.objectif_valeur} onChange={e => setChallengeForm({ ...challengeForm, objectif_valeur: e.target.value })} type="number" required /></div>
                                <div className="form-group"><label>Points recompense</label><input value={challengeForm.points_recompense} onChange={e => setChallengeForm({ ...challengeForm, points_recompense: e.target.value })} type="number" /></div>
                            </div>
                            <div className="form-row">
                                <div className="form-group"><label>Debut</label><input type="date" value={challengeForm.date_debut} onChange={e => setChallengeForm({ ...challengeForm, date_debut: e.target.value })} required /></div>
                                <div className="form-group"><label>Fin</label><input type="date" value={challengeForm.date_fin} onChange={e => setChallengeForm({ ...challengeForm, date_fin: e.target.value })} required /></div>
                            </div>
                            <div className="modal-actions">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowChallengeModal(false)}>Annuler</button>
                                <button type="submit" className="btn btn-primary">Creer</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Gamification;
